import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import useMondayContext from "../../hooks/context/useMondayContext";
import useConsent from "../../hooks/api/useConsent";
import useFreeMonthOffer from "../../hooks/api/useFreeMonthOffer";
import { MondayApiQueryInstance } from "../../services/monday/monday-service-ts";

/**
 * The offer opens from the full surfaces only; small Monday iframes (batch and
 * item menu actions, column extensions, settings) are too cramped for it.
 */
const OFFER_SURFACES = ["AppFeatureItemView", "AppFeatureBoardView"];

export const OFFER_PATH = "/free-month-offer";
// Tall enough for the redeem step's video; the page scrolls if monday clamps it.
const OFFER_MODAL_HEIGHT = "720px";
const OFFER_MODAL_WIDTH = "600px";

// The item view and the board view can both be open in one tab and load at the
// same moment. sessionStorage is shared between same-origin iframes, so a short
// lock keeps them from both opening it. It has to expire: a tab opened from
// monday inherits sessionStorage, and a lasting flag hid the offer there. Once
// the modal closes the backend stops offering, so nothing reopens it anyway.
const OPENING_KEY = "simb:freeMonthOfferOpeningAt";
const OPENING_LOCK_MS = 30_000;

const claimOpen = (now = Date.now()) => {
  try {
    const openingAt = Number(sessionStorage.getItem(OPENING_KEY));
    if (openingAt && now - openingAt < OPENING_LOCK_MS) return false;
    sessionStorage.setItem(OPENING_KEY, String(now));
  } catch {
    // Storage blocked: fall back to once per iframe.
  }
  return true;
};

/**
 * First-month-free offer. Mounted once near the app root, after ConsentGate.
 * The backend decides everything — eligibility, the control cohort, the grant —
 * so nothing happens unless the status says `showOffer`. Then it opens
 * FreeMonthOfferPage in monday's app feature modal, over the whole monday page
 * rather than inside this iframe. Closing it is final: the backend never offers
 * it to this admin again.
 */
const FreeMonthOffer = () => {
  const { appFeatureType } = useMondayContext();
  const { needsConsent, isLoading: consentLoading } = useConsent();
  const { pathname } = useLocation();

  // Only from the surface's own page, never from inside another app modal
  // (including the offer's), and never stacked on top of the consent gate.
  const enabled =
    pathname === "/" &&
    OFFER_SURFACES.includes(appFeatureType ?? "") &&
    !consentLoading &&
    needsConsent === false;
  const { status, dismiss, refresh } = useFreeMonthOffer(enabled);

  const opened = useRef(false);
  const showOffer = enabled && !!status?.showOffer;

  useEffect(() => {
    if (!showOffer || opened.current) return;
    opened.current = true;
    if (!claimOpen()) return;

    void (async () => {
      try {
        // Resolves once the modal closes.
        await MondayApiQueryInstance.openAppFeatureModal(
          OFFER_PATH,
          null,
          OFFER_MODAL_HEIGHT,
          OFFER_MODAL_WIDTH,
        );
      } catch {
        return;
      }
      // Still on offer means it was closed with monday's X without a choice:
      // that counts as a dismissal, as the in-app close button did.
      const latest = await refresh();
      if (latest?.showOffer) await dismiss();
    })();
  }, [showOffer, dismiss, refresh]);

  return null;
};

export default FreeMonthOffer;
